import type { TypePolicies } from '@apollo/client';

/**
 * Single source of truth for how many change-log entries every consumer of
 * GET_MEMBERSHIP_CHANGE_LOGS requests. All call sites must use this limit
 * (with offset 0) so they share one cache entry and every mutation's
 * refetchQueries refreshes every consumer.
 *
 * History: one hook fetched with limit 20 while the billing history screen
 * fetched with limit 50. The old cache policy keyed only on userId, so the
 * 20-entry response satisfied the 50-entry cache-first query and users lost
 * their newest billing entries (2026-07 prod incident).
 */
export const CHANGE_LOG_QUERY_LIMIT = 200;

export const typePolicies: TypePolicies = {
    Product: {
        // Use composite key: product ID + pricing context
        // This prevents products from different queries overwriting each other
        keyFields: (object: any) => {
            // Safely extract values with fallbacks
            const id = object?.id || 'unknown';
            const purchasableQuery = object?.pricing?.isPurchasableQuery ?? false;
            return `${id}:${purchasableQuery}`;
        },
        fields: {
            pricing: {
                // Don't merge pricing data - always replace it
                merge(_existing, incoming) {
                    return incoming;
                },
            }
        }
    },
    Price: {
        keyFields: ['id'],
    },
    Pricing: {
        keyFields: false, // Value object, no key
    },
    UserProfile: {
        keyFields: ['id'], // Use user ID for cache keys
    },
    Membership: {
        keyFields: ['id'], // Use membership ID for cache keys
    },
    Query: {
        fields: {
            getProducts: {
                keyArgs: false,
                merge(_, incoming) {
                    return incoming;
                },
            },
            getUserProfile: {
                keyArgs: ['id'],
                merge(_, incoming) {
                    return incoming;
                },
            },
            getMembershipChangeLogs: {
                // limit/offset are part of the cache key so a smaller fetch can
                // never satisfy (or clobber) a larger one. The server returns
                // newest-first; each response fully replaces the cached list for
                // its exact args. Do NOT swap back to offsetLimitPagination: its
                // positional merge assumes append-only ASC data and corrupts the
                // list under newest-first ordering.
                keyArgs: ['userId', 'limit', 'offset'],
                merge(_: unknown, incoming: unknown) {
                    return incoming;
                },
            },
        },
    },
};
